import React, { useEffect, useRef, useState } from 'react';
import Amplify, { Hub } from 'aws-amplify';
import { withAuthenticator } from '@aws-amplify/ui-react';
import { SnackbarProvider } from 'notistack';
import PubSub from 'pubsub-js';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { Routes, Route } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';

import './App.css';
import '@aws-amplify/ui-react/styles.css';

import { CLIENT_EVENTS, USER_STREAM_STATUS } from './common/constants';
import awsconfig from './common/aws-exports';
import UserStream from './common/services/userStream';
import {
	AllCharacterTemplates,
	AllCharacters,
	AdventureEditor,
	Character,
	CharacterTemplate,
	Game,
	Home,
	Main,
} from './pages/index';
import { themeGenerator } from './common/utils';
import { debugSession } from './common/utils/sessionDebug';

Amplify.configure(awsconfig);

const THEME_MODE_STORAGE_KEY = 'horrol-theme-mode';

let roomConnections = [];

function App() {
	const [client, setWebsocket] = useState({});
	const [id, setId] = useState(null);
	const [socketUrl, setSocketUrl] = useState();
	const [localMedia, setMedia] = useState();
	const pingIntervalRef = useRef(null);
	const [themeMode, setThemeMode] = useState(() => {
		const storedMode = localStorage.getItem(THEME_MODE_STORAGE_KEY);
		return storedMode === 'dark' ? 'dark' : 'light';
	});

	const theme = themeGenerator(createTheme, themeMode);

	useEffect(() => {
		debugSession('App', 'mount', {
			pathname: window.location.pathname,
		});

		return () => {
			debugSession('App', 'unmount', {
				pathname: window.location.pathname,
			});
		};
	}, []);

	const toggleThemeMode = () => {
		setThemeMode((previousMode) => {
			const nextMode = previousMode === 'light' ? 'dark' : 'light';
			localStorage.setItem(THEME_MODE_STORAGE_KEY, nextMode);
			return nextMode;
		});
	};

	const { sendJsonMessage, lastJsonMessage } = useWebSocket(socketUrl, {
		onOpen: () => {
			debugSession('App', 'websocket-open', {
				socketUrl: socketUrl || null,
				pathname: location.pathname,
			});
			setWebsocket({ send: sendJsonMessage });
			if (pingIntervalRef.current) {
				clearInterval(pingIntervalRef.current);
			}
			pingIntervalRef.current = setInterval(() => {
				ping();
			}, 60 * 1000 * 7);
			const adventure = location.pathname.replace(gameSessionPath, '').replace('/', '');

			if (location.pathname.startsWith(gameSessionPath)) {
				debugSession('App', 'send-room-join', {
					room: adventure,
					pathname: location.pathname,
				});
				sendJsonMessage({
					action: 'room',
					username: 'username',
					message: 'some weird message',
					room: adventure,
				});
			}
		},
		//Will attempt to reconnect on all close events, such as server shutting down
		shouldReconnect: (closeEvent) => true,
	}, Boolean(socketUrl));

	useEffect(() => {
		debugSession('App', 'socket-url-changed', {
			socketUrl: socketUrl || null,
		});

		return () => {
			if (pingIntervalRef.current) {
				clearInterval(pingIntervalRef.current);
				pingIntervalRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		const removeHubListener = Hub.listen('auth', ({ payload }) => {
			debugSession('Auth', 'hub-event', {
				event: payload?.event || null,
				message: payload?.message || null,
			});
		});

		const handleWindowError = (event) => {
			debugSession('Window', 'error', {
				message: event.message,
				filename: event.filename,
				lineno: event.lineno,
				colno: event.colno,
			});
		};

		const handleUnhandledRejection = (event) => {
			debugSession('Window', 'unhandledrejection', {
				reason:
					event.reason instanceof Error
						? {
								message: event.reason.message,
								stack: event.reason.stack,
							}
						: event.reason,
			});
		};

		window.addEventListener('error', handleWindowError);
		window.addEventListener('unhandledrejection', handleUnhandledRejection);

		return () => {
			removeHubListener?.();
			window.removeEventListener('error', handleWindowError);
			window.removeEventListener('unhandledrejection', handleUnhandledRejection);
		};
	}, []);

	const ping = () => {
		debugSession('App', 'ping-send', {
			socketUrl: socketUrl || null,
		});
		sendJsonMessage({
			action: 'ping',
		});
	};

	const getRoomConnection = (remoteId) => {
		return roomConnections.find((connection) => connection.id === remoteId);
	};

	const createRoomConnection = (newConnection) => {
		if (!localMedia || !newConnection?.id || getRoomConnection(newConnection.id)) {
			debugSession('App', 'skip-create-room-connection', {
				hasLocalMedia: Boolean(localMedia),
				newConnectionId: newConnection?.id || null,
				alreadyExists: Boolean(newConnection?.id && getRoomConnection(newConnection.id)),
			});
			return getRoomConnection(newConnection?.id);
		}

		debugSession('App', 'create-room-connection', {
			remoteId: newConnection.id,
			status: newConnection.status,
		});
		const newConnectionStream = new UserStream({
			client: client,
			id: newConnection.fromId || id,
			remoteId: newConnection.id,
			status: newConnection.status,
			initialData: newConnection.initialData,
			addStream: addStream,
			addDataChannel: addDataChannel,
			addNewRemoteMessage: addNewRemoteMessage,
			removeStream: removeStream,
			removeDataChannel: removeDataChannel,
			localMedia: localMedia,
		});
		roomConnections.push(newConnectionStream);
		return newConnectionStream;
	};

	useEffect(() => {
		const addClients = (newClients, myId, status = 'pasive', data = undefined) => {
			debugSession('App', 'add-clients', {
				newClients,
				myId,
				status,
			});
			const newClientStatus = newClients
				.filter((remoteId) => !roomConnections.some((x) => x.id === remoteId))
				.map((remoteId) => {
					return {
						id: remoteId,
						status: status,
						initialData: data,
						fromId: myId,
					};
				});
			setRoomConections(newClientStatus);
		};

		if (lastJsonMessage) {
			debugSession('App', 'last-json-message', {
				event: lastJsonMessage.event || null,
				action: lastJsonMessage.action || null,
				type: lastJsonMessage.type || null,
				id: lastJsonMessage.id || null,
				fromId: lastJsonMessage.fromId || null,
			});
			if (lastJsonMessage.event === 'room-update') {
				setId(lastJsonMessage.id);
				const clientsButMe = lastJsonMessage.clients.filter(
					(x) => x !== lastJsonMessage.id
				);
				addClients(clientsButMe, lastJsonMessage.id);
			} else if (lastJsonMessage.action === 'signaling') {
				if (lastJsonMessage.type === 'ping') {
					const existingConnection = getRoomConnection(lastJsonMessage.fromId);
					if (existingConnection?.handleIncomingPing) {
						existingConnection.handleIncomingPing(lastJsonMessage);
					} else {
						addClients(
							[lastJsonMessage.fromId],
							lastJsonMessage.id,
							'active',
							lastJsonMessage
						);
					}
				} else {
					const user =
						getRoomConnection(lastJsonMessage.fromId) ||
						createRoomConnection({
							id: lastJsonMessage.fromId,
							status: 'active',
							initialData: lastJsonMessage,
							fromId: lastJsonMessage.id || id,
						});
					if (user?.gotMessageFromSignaling) {
						user.gotMessageFromSignaling('message', lastJsonMessage);
					}
				}
			}
		}
	}, [lastJsonMessage]);

	const gameSessionPath = '/game';

	let mediaStreams = [];

	const addStream = (remoteId, stream) => {
		mediaStreams.push({
			id: remoteId,
			streamMedia: stream,
		});
		var event = new CustomEvent('newStreamMedia', {
			detail: {
				id: remoteId,
				streamMedia: stream,
			},
		});
		document.dispatchEvent(event);
	};

	const addDataChannel = (remoteId, channel) => {
		var event = new CustomEvent('newDataChannel', {
			detail: {
				id: remoteId,
				channel: channel,
			},
		});
		document.dispatchEvent(event);
	};

	const removeDataChannel = (remoteId) => {
		var event = new CustomEvent('removeDataChannel', {
			detail: {
				id: remoteId,
			},
		});
		document.dispatchEvent(event);
	};

	const addNewRemoteMessage = (remoteId, message) => {
		var event = new CustomEvent('newDataChannelMessage', {
			detail: {
				id: remoteId,
				message: message,
			},
		});
		document.dispatchEvent(event);
	};

	const removeStream = (remoteId) => {
		var event = new CustomEvent('removeStream', {
			detail: {
				id: remoteId,
			},
		});
		document.dispatchEvent(event);
	};
	const refetchRoomUsers = (remoteId) => {
		var event = new CustomEvent('refetchRoomUsers', {
			detail: {
				id: remoteId,
			},
		});
		document.dispatchEvent(event);
	};

	const setLocalMedia = (media) => {
		debugSession('App', 'set-local-media', {
			hasMedia: Boolean(media),
			socketUrl: socketUrl || null,
		});
		if (!socketUrl) {
			if (process.env.REACT_APP_API_ENV === 'prod') {
				setSocketUrl('wss://jdr4lgovx3.execute-api.eu-west-1.amazonaws.com/prod');
			} else {
				setSocketUrl('wss://luaw2cnx3b.execute-api.eu-west-1.amazonaws.com/stg');
			}
		}
		roomConnections.map((x) => x.updateLocalMedia(media));
		setMedia(media);
	};

	const setRoomConections = (newRoomConnection) => {
		debugSession('App', 'set-room-connections', {
			incomingCount: newRoomConnection?.length || 0,
			existingCount: roomConnections.length,
			hasLocalMedia: Boolean(localMedia),
			id,
		});
		if (roomConnections.length > 0) {
			//debugger;
		}

		if (localMedia && newRoomConnection.length > 0) {
			roomConnections
				.filter((oldConnection) =>
					newRoomConnection.some((x) => {
						return (
							oldConnection.status === USER_STREAM_STATUS.CREATED &&
							x.id === oldConnection.id
						);
					})
				)
				.forEach((x) => {
					x.pong();
				});
				newRoomConnection
					.filter((x) => !roomConnections.some((oldConnection) => oldConnection.id === x.id))
					.map((newConnection) => {
						const newConnectionStream = createRoomConnection(newConnection);
						return roomConnections;
					});
			}
		};

	const retrieveLogs = (ids = []) => {
		return roomConnections
			.filter((x) => ids.length === 0 || ids.includes(x.id))
			.map((x) => {
				return { id: x.id, logs: x.logs };
			});
	};

	const resyncUser = (event, id) => {
		debugSession('App', 'resync-user', {
			id,
		});
		const tempStream = roomConnections.find((x) => {
			return x.id === id;
		});

		tempStream.createStream();
		tempStream.setDataChannel();
		tempStream.resync();
	};

	const deleteUser = (event, id) => {
		debugSession('App', 'delete-user', {
			id,
		});
		const tempStream = roomConnections.find((x) => {
			return x.id === id;
		});

		try {
			tempStream.delete();
		} catch (e) {
			console.error('error', e);
		}

		roomConnections = roomConnections.filter((x) => {
			return x.id !== id;
		});

		mediaStreams = mediaStreams.filter((x) => {
			return x.id !== id;
		});
		removeStream(id);
		setTimeout(refetchRoomUsers, 5000);
	};

	let location = useLocation();

	useEffect(() => {
		debugSession('App', 'location-change', {
			pathname: location.pathname,
		});
		if (location.pathname !== gameSessionPath) {
			clearRoomConnections();
			if (client.readyState === 1) {
				client.send(
					JSON.stringify({
						action: 'leaveRoom',
					})
				);
			}
		}
	}, [location]);

	const clearRoomConnections = () => {
		debugSession('App', 'clear-room-connections', {
			connectionCount: roomConnections.length,
			clientReadyState: client.readyState,
		});
		roomConnections.map((x) => {
			x.delete();

			x = null;
		});

		while (roomConnections.length) {
			roomConnections.pop();
		}
		if (client.readyState === 1) {
			client.close();
		}
		if (pingIntervalRef.current) {
			clearInterval(pingIntervalRef.current);
			pingIntervalRef.current = null;
		}
	};

	useEffect(() => {
		debugSession('App', 'pubsub-subscribe', {
			id,
			hasClient: Boolean(client),
			hasLocalMedia: Boolean(localMedia),
		});
		const resyncToken = PubSub.subscribe(CLIENT_EVENTS.RESYNC_USER, resyncUser);
		const deleteToken = PubSub.subscribe(CLIENT_EVENTS.DELETE_USER, deleteUser);

		return () => {
			debugSession('App', 'pubsub-unsubscribe', {
				id,
			});
			PubSub.unsubscribe(resyncToken);
			PubSub.unsubscribe(deleteToken);
		};
	}, [id, client, localMedia]);

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={theme}>
				<SnackbarProvider maxSnack={7} preventDuplicate>
					<Routes>
							<Route
								path="/"
								element={
									<Main
										themeMode={themeMode}
										toggleThemeMode={toggleThemeMode}
									/>
								}
							>
								<Route index element={<Home />} />
								{/* <Route path=":teamId" element={3} />
								<Route path="new" element={4} /> */}
								<Route
									path="/adventure/:adventureId"
									element={<AdventureEditor />}
								/>
								<Route path="/character/:characterId" element={<Character />} />
								<Route
									path="/create_character/:templateId"
									element={<Character />}
								/>
								<Route path="/character_template" element={<CharacterTemplate />} />
								<Route
									path="/character_template/:templateId"
									element={<CharacterTemplate />}
								/>
								<Route
									path="/character_templates"
									element={<AllCharacterTemplates />}
								/>
								<Route path="/characters" element={<AllCharacters />} />
							</Route>
							<Route
								path="game"
								element={
									<Game
										client={client}
										retrieveLogs={retrieveLogs}
										setId={setId}
										id={id}
										setLocalMedia={setLocalMedia}
										setRoomConections={setRoomConections}
										resyncUser={resyncUser}
									/>
								}></Route>
							<Route
								path="/game/:roomId"
								element={
									<Game
										client={client}
										retrieveLogs={retrieveLogs}
										setId={setId}
										id={id}
										setLocalMedia={setLocalMedia}
										setRoomConections={setRoomConections}
										resyncUser={resyncUser}
									/>
								}></Route>
					</Routes>
				</SnackbarProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	);
}

export default withAuthenticator(App);
